/**
 * Formats a price as a USD currency string (e.g., "$12.00").
 */
export function formatPrice(price: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
    }).format(price)
}

/**
 * Formats a whole-dollar price without cents (e.g., "$12").
 */
export function formatPriceWhole(price: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
    }).format(price)
}

/**
 * Formats a `YYYY-MM-DD` date string as a short display date (e.g. "Thu, Feb 12").
 *
 * SSR-safe by construction (invariants.md#ssr-safe-rendered-dates): the `Date` is
 * built from explicit Y/M/D parts at **local noon** and formatted in that same local
 * zone, so the rendered weekday/month/day equals the input string's calendar date in
 * *every* timezone — identical on the server-zone SSR render and the browser-zone
 * client render. Do NOT switch to `new Date(iso)` (a UTC parse) or drop the noon
 * anchor: either makes the output timezone-dependent and reintroduces a React #418
 * hydration mismatch. Input must be a `YYYY-MM-DD` string, never the ambient clock.
 */
export function formatShortDate(iso: string): string {
    const [year, month, day] = iso.split('-').map(Number)
    return new Date(year, month - 1, day, 12, 0, 0).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    })
}

/**
 * Formats duration in minutes to a human-readable string (e.g., "2h 30m").
 * Omits the hours part if zero, and the minutes part if zero.
 */
export function formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours === 0) {
        return `${mins}m`
    }
    if (mins === 0) {
        return `${hours}h`
    }
    return `${hours}h ${mins}m`
}
